import { useEffect, useState } from 'react';
import { useT } from '@/i18n';
import { usePlan, type Branding } from '@/store/plan';
import { schriftAuf } from '@/lib/farbe';

/**
 * Verbandshinweis (Harald/Daniel, 09/2026): „Kannst du vielleicht noch das
 * AIC-Logo unterbringen?" Gültig ist „Lizenziert durch die AIC".
 *
 * Bild und Wortlaut stehen als zwei Konstanten hier oben — für einen anderen
 * Wortlaut ändert man `TEXT` und die drei Wörterbucheinträge in `dict/core.ts`,
 * sonst nichts. Genau das war schon einmal nötig (es fing mit „Unterstützt vom
 * AIC" an), deshalb steht es bewusst an einer Stelle.
 *
 * **Fehlt die Datei, bleibt nur die Zeile stehen** und nichts bricht:
 * `onError` blendet das Bild aus, statt ein kaputtes Symbol zu zeigen.
 *
 * Zwei Dateien, in dieser Reihenfolge:
 *
 * 1. **`/aic-logo.svg`** — die Vektorfassung (Daniel, 09/2026): scharf auf dem
 *    Beamer, transparent, ohne Kachel.
 * 2. **`/aic-logo.png`** als Rückfall, solange das SVG nicht im Build liegt.
 *    Diese Datei hat einen **deckend weißen Hintergrund** (kein Alpha); auf der
 *    dunklen Leiste und auf dem Beamer stünde sonst ein weißer Klotz herum, der
 *    aussieht wie ein Ladefehler. Deshalb liegt sie bewusst auf einer weißen
 *    Kachel — so wirkt das Weiß gewollt.
 *
 * In beiden steckt der Schriftzug **im Bild** („Associazione Italiana
 * Cornhole"). Unter etwa 40 px Höhe ist er Matsch, deshalb steht das Logo über
 * der Zeile statt daneben und bekommt Platz.
 *
 * **Eigenes Logo (docs/21, nur Pro) steht hier bewusst nicht.** Es stand erst
 * links neben der AIC — Daniel (09/2026): „dort unten geht es verloren, macht
 * die Leiste voll und lenkt vom ‚Lizenziert durch die AIC‘ ab.“ Jetzt ersetzt
 * es die Airmail-Marke oben in der Seitenleiste und steht in der Kopfzeile von
 * Beamer, Aushang, Plakat, Anmelde- und Zuschauerseite (`EigenesLogo`). Der
 * Verbandshinweis bleibt so allein und ruhig.
 */
const LOGOS = ['/aic-logo.svg', '/aic-logo.png'] as const;
const TEXT = 'Lizenziert durch die AIC';

/** Höhe des Logos je Ort — unter 40 px zerfällt der Schriftzug darin */
const HOEHE = { leiste: 40, beamer: 56, druck: 30 } as const;

/**
 * Branding des eigenen Kontos — nur, wenn die Stufe es hergibt. Läuft die
 * Testphase ab, verschwinden Logo und Farbe von selbst; der Server liefert
 * dann `features.branding = false`, das gespeicherte Branding bleibt liegen.
 */
export function useKontoBranding(): Branding {
  const { konto, features } = usePlan();
  return features.branding && konto ? konto.branding : {};
}

/*
 * Akzentfarbe: `--color-red` ist die eine rote Variable der App (index.css),
 * Tailwind-Klassen wie `bg-red` lesen sie zur Laufzeit. Ein Inline-Stil auf
 * `<html>` schlägt beide Themes, also färben sich Beamer, Aushänge, Plakat und
 * Knöpfe von selbst — die Schrift auf den Knöpfen (`--color-on-red`) wird
 * dazu hell oder dunkel gewählt. Fehlermeldungen lesen `--color-err` und
 * bleiben rot, egal welche Farbe der Akzent hat. Zwei Quellen: das Konto (überall) und die öffentliche
 * Seite (Live, Anmelden) — die Seite gewinnt, damit das Publikum die Farbe des
 * Veranstalters sieht, nicht die des Kontos, das am Handy gerade angemeldet ist.
 */
const akzent: { konto?: string; seite?: string } = {};
function akzentAnwenden(): void {
  const farbe = akzent.seite ?? akzent.konto;
  const root = document.documentElement;
  if (farbe) {
    root.style.setProperty('--color-red', farbe);
    // Knopfschrift passend zur Farbe: dunkler Akzent → helle Schrift (lib/farbe.ts)
    root.style.setProperty('--color-on-red', schriftAuf(farbe));
  } else {
    root.style.removeProperty('--color-red');
    root.style.removeProperty('--color-on-red');
  }
}
const FARBE = /^#[0-9a-f]{6}$/i;

/** Akzentfarbe auf den Root — nur `#rrggbb`, sonst bleibt das Rot */
export function useAkzentfarbe(farbe: string | undefined, quelle: 'konto' | 'seite' = 'seite'): void {
  const gueltig = farbe && FARBE.test(farbe) ? farbe : undefined;
  useEffect(() => {
    akzent[quelle] = gueltig;
    akzentAnwenden();
    return () => {
      akzent[quelle] = undefined;
      akzentAnwenden();
    };
  }, [gueltig, quelle]);
}

/** Hängt einmal im Rahmen (AuthProvider): die Akzentfarbe des Kontos, solange Pro gilt */
export function KontoBranding() {
  useAkzentfarbe(useKontoBranding().farbe, 'konto');
  return null;
}

/** Das eigene Logo allein — für Kopfzeilen, in denen kein AIC-Hinweis steht */
export function EigenesLogo({ logo, hoehe, className, alt = '' }: { logo: string | undefined; hoehe: number; className?: string; alt?: string }) {
  if (!logo) return null;
  return <img src={logo} alt={alt} data-eigenes-logo className={className} style={{ height: hoehe, width: 'auto', maxWidth: hoehe * 4, objectFit: 'contain' }} />;
}

export function Partner({ variant = 'leiste' }: { variant?: keyof typeof HOEHE }) {
  const t = useT();
  // Welche der Dateien gerade dran ist; hinter der letzten bleibt nur die Zeile
  const [nr, setNr] = useState(0);
  const logo = LOGOS[nr];
  const druck = variant === 'druck';
  // Kachel nur unter dem PNG — und auf Papier nie, dort ist der Untergrund ohnehin weiß
  const kachel = !druck && logo === '/aic-logo.png';
  return (
    <div className={druck ? 'partner-druck' : 'grid gap-1.5 justify-items-start'} title={t('Associazione Italiana Cornhole')}>
      <span className="inline-flex items-center gap-3">
        {logo && (
          <span className={kachel ? 'bg-white px-1.5 py-1 inline-flex rounded-[2px]' : 'inline-flex'}>
            <img key={logo} src={logo} alt="AIC" style={{ height: HOEHE[variant], width: 'auto' }} onError={() => setNr((n) => n + 1)} loading="lazy" />
          </span>
        )}
      </span>
      <span className={druck ? 'druck-sub' : variant === 'beamer' ? 'label text-[11px]' : 'text-[10px] text-dim leading-tight'}>{t(TEXT)}</span>
    </div>
  );
}
